import { ChevronDown } from '@carbon/icons-react'
import { useToken, Box, Tooltip, Text, Flex } from '@chakra-ui/react'
import ReactSelect, { StylesConfig } from 'react-select'
import theme from 'theme'

export type SelectOption<T> = { label: string; value: T }

export const Select = ({
  value,
  onChange,
  height,
  isMulti,
  options,
  isSearchable,
  backgroundColor,
  controlBackground,
  color,
  borderColor,
  placeholder,
  fontSize,
  noBorder,
  menuHeight,
  isOptionDisabled,
  CustomValueContainer,
  CustomOption,
  width,
  isDisabled,
  isLoading,
  backspaceRemovesValue = true,
}: {
  value?: any
  isMulti?: boolean
  height?: string
  onChange?: any
  options?: any
  isSearchable?: boolean
  backgroundColor?: string
  controlBackground?: string
  width?: string
  color?: string
  borderColor?: string
  fontSize?: string
  placeholder?: string
  noBorder?: boolean
  menuHeight?: string
  isOptionDisabled?: (option: any) => boolean
  CustomValueContainer?: ({ children, ...props }: any) => JSX.Element
  CustomOption?: (props: any) => JSX.Element
  isDisabled?: boolean
  isLoading?: boolean
  backspaceRemovesValue?: boolean
}) => {
  const primaryColor = useToken('colors', 'primary')

  const customStyles: Partial<StylesConfig> = {
    placeholder: (provided: any, state: any) => ({
      ...provided,
      color: color,
    }),
    menu: (provided: any, state: any) => ({
      ...provided,
      width: 'max-content',
      maxWidth: '400px',
      minWidth: '200px',

      color: state.selectProps.menuColor,
      padding: theme.sizes[1],
      fontSize: fontSize || theme.fontSizes.sm,
      cursor: 'pointer',
      borderRadius: '6px',
    }),
    menuList: (provided: any, state: any) => ({
      ...provided,
      ...(menuHeight && { maxHeight: `${menuHeight}` }),
    }),
    menuPortal: (provided: any, state: any) => ({
      ...provided,
      ...(menuHeight && { maxHeight: `${menuHeight}` }),
      zIndex: 9990,
    }),

    option: (provided: any, state: any) => ({
      ...provided,
      borderColor: borderColor ? borderColor : theme.colors.gray[200],
      backgroundColor: backgroundColor
        ? backgroundColor
        : state.isFocused
          ? theme.colors.gray[100]
          : state.isSelected
            ? theme.colors.gray[100]
            : state.isActive
              ? theme.colors.gray[200]
              : 'white',
      cursor: 'pointer',
      height: 'auto',
      overflow: 'hidden',
      fontSize: fontSize || theme.fontSizes.sm,
      maxWidth: '400px',
      zIndex: 60,
      fontWeight: 500,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: color ? color : theme.colors.gray[800],
      borderRadius: theme.radii.md,
      '&:active': {
        backgroundColor: theme.colors.gray[200],
        color: color ? color : theme.colors.gray[800],
      },
    }),

    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: height ? height : '32px',
      fontSize: fontSize || theme.fontSizes.sm,
      outline: 'none',
      fontWeight: 500,
      borderColor: borderColor
        ? borderColor
        : state.isFocused
          ? theme.colors.primary[400]
          : theme.colors.gray[200],
      backgroundColor: controlBackground || backgroundColor || 'transparent',
      width: width ?? 'fit',
      maxHeight: '400px',
      overflowY: 'auto',
      cursor: 'pointer',
      boxShadow: noBorder ? 'none' : '',
      border: noBorder ? 'none' : '',
    }),

    singleValue: (provided: any, state: any) => {
      const opacity = state.isDisabled ? 0.5 : 1
      const transition = 'opacity 300ms'

      return {
        ...provided,
        opacity,
        transition,
        color: primaryColor,
        fontWeight: 400,
        fontSize: fontSize || 13,
      }
    },

    valueContainer: (provided: any, state: any) => {
      return { ...provided, paddingLeft: '12px' }
    },
  }

  return (
    <ReactSelect
      isDisabled={isDisabled || false}
      isLoading={isLoading}
      styles={customStyles}
      value={value}
      isMulti={isMulti}
      formatOptionLabel={CustomLabelOption}
      onChange={onChange}
      options={options}
      {...(placeholder ? { placeholder } : {})}
      isSearchable={isSearchable}
      classNamePrefix='react-select'
      menuPortalTarget={document.body} // ✅ use this instead of a custom portal
      menuPosition='fixed' // ✅ important to ensure corr
      isOptionDisabled={isOptionDisabled}
      backspaceRemovesValue={backspaceRemovesValue}
      components={{
        ...(CustomOption ? { Option: CustomOption } : {}),
        ...(CustomValueContainer
          ? { ValueContainer: CustomValueContainer }
          : {}),
        IndicatorsContainer: () => (
          <Box mr='12px'>
            <ChevronDown size={16} />
          </Box>
        ),

        DownChevron: () => null,
        IndicatorSeparator: () => null,
        ClearIndicator: () => null,
      }}
      hideSelectedOptions={false}
    />
  )
}

const CustomLabelOption = ({ label }: any) => {
  if (label?.length > 50) {
    return (
      <Tooltip label={label}>
        <Text>{label}</Text>
      </Tooltip>
    )
  }
  return (
    <Flex>
      <Text>{label}</Text>
    </Flex>
  )
}
